import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send cookies on cross-origin requests (httpOnly access_token).
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = not auth, obj = authed
  const [booted, setBooted] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      // 401 is expected when not logged in; don't noise the console.
      if (err?.response?.status && err.response.status !== 401) {
        // eslint-disable-next-line no-console
        console.error("[auth] /auth/me failed:", err);
      }
      setUser(null);
    } finally {
      setBooted(true);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = useCallback(async (email, password) => {
    // Server sets an httpOnly cookie; body also contains user + (legacy) token.
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] logout failed (clearing client state anyway):", err);
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, booted, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
